/**
 * 検知の利用状況インジケーター2種（FR-16-5）。
 *
 * ⚠️⚠️ **AIO分析の進捗コンテキストからは「ポーリングの形」だけを借り、追跡機構は借りない。** ⚠️⚠️
 *
 * 流用元（`analysis-progress-context.tsx`）はバッチを**個別に追跡**し、一括進捗APIの応答に
 * 現れなくなったバッチを「取得不能」とみなして猶予期間後にエラートーストを出す。しかし
 * **完了したバッチも応答から消える**ため、完了を別経路で検出できないと
 * **正常終了なのにエラーが出る**（実際に起きていた）。さらに完了バッチの削除タイマーを
 * 再レンダーをまたいで永続保持していないと消えない不具合もあった。
 *
 * ここでは**その構造ごと持ち込まない**:
 * - サーバから受け取るのは**集計値だけ**（`running` / 使用量）。個別の実行を追跡しない
 * - よって「応答から消えた」を判定する場面が無く、**誤検出のしようがない**
 * - タイマーも保持しない（追跡しないので消す対象が無い）
 *
 * **完了の通知はここの責務ではない**。トースト・ステータスバッジは実行ステータスの経路
 * （`useHallucinationExecutionStatus`）が担う。ドットは `running` が減るだけ。
 *
 * **開始時にドットを楽観的に加算しない**。受付とサーバ反映のズレで数がぶれるため、
 * 表示は常にサーバの値に従う。
 */
import { useQuery } from '@tanstack/react-query'
import {
  getHallucinationDetectionProgress,
  getHallucinationDetectionReadiness,
} from '@/api/requests/hallucination-detection'
import { HALLUCINATION_POLL_INTERVAL_MS } from '@/config/hallucination-limits'
import type {
  DetectionProgressResponse,
  DetectionReadinessResponse,
} from '@/types/api/hallucination-detection'

/** 月間利用量バーの状態（画面設計 §7.2 の3状態）。 */
export type HallucinationQuotaLevel = 'normal' | 'caution' | 'exhausted'

export interface UseHallucinationUsageResult {
  currentUsage: number
  /** 無制限なら null。 */
  monthlyLimit: number | null
  /** 無制限なら null。 */
  remainingUnits: number | null
  /** バーの塗り（0〜100）。無制限は 0（＝満ちていない）。 */
  usedPercentage: number
  isLimitActive: boolean
  /**
   * 3状態。⚠️ **閾値は割合ではなく「1回の実行上限」基準**（画面設計 §7.2）。
   * 月間枠は Space 個別に上書きできるため、割合で切ると枠の小さい Space で
   * 「注意が出る前に実行が通らない」状態に入る。
   */
  level: HallucinationQuotaLevel
  /** 1回の実行上限（注意表示の閾値。応答から取りハードコードしない）。 */
  maxPerRun: number
  isLoading: boolean
  error: Error | null
}

/**
 * 月間利用量バー（インジケーター②）。
 *
 * ⚠️ **準備チェックの応答から取る**（専用EPは作らない・基本設計 §7.2a）。
 * 注意表示が「残枠 < 1回の実行上限」という**2値の比較**で決まるため、
 * 上限値と残量を別経路で取ると食い違う。
 */
export function useHallucinationUsage(
  spaceId?: string,
  serviceId?: string
): UseHallucinationUsageResult {
  const { data, isLoading, error } = useQuery<DetectionReadinessResponse | null>({
    queryKey: ['hallucination-detection-readiness', spaceId, serviceId],
    queryFn: async ({ signal }) => {
      if (!spaceId || !serviceId) return null
      return getHallucinationDetectionReadiness(spaceId, serviceId, signal)
    },
    enabled: Boolean(spaceId && serviceId),
    refetchInterval: HALLUCINATION_POLL_INTERVAL_MS,
  })

  const quota = data?.quota
  const currentUsage = quota?.currentMonthlyUsage ?? 0
  const monthlyLimit = quota?.monthlyLimit ?? null
  const remainingUnits = quota?.remainingUnits ?? null
  const maxPerRun = data?.maxCombinations ?? 0

  // 無制限は「満ちていない」＝0%。上限0のゼロ除算も避ける。
  const usedPercentage =
    monthlyLimit === null || monthlyLimit <= 0
      ? 0
      : Math.max(0, Math.min(100, (currentUsage / monthlyLimit) * 100))

  const level: HallucinationQuotaLevel =
    remainingUnits === null
      ? 'normal'
      : remainingUnits <= 0
        ? 'exhausted'
        : remainingUnits < maxPerRun
          ? 'caution'
          : 'normal'

  return {
    currentUsage,
    monthlyLimit,
    remainingUnits,
    usedPercentage,
    isLimitActive: quota?.isLimitActive ?? true,
    level,
    maxPerRun,
    isLoading,
    error: (error as Error | null) ?? null,
  }
}

export interface UseHallucinationProgressResult {
  /** 進行中の検知実行数（サーバの集計値。楽観更新しない）。 */
  running: number
  /** 同時実行上限。 */
  limit: number
  isLoading: boolean
  error: Error | null
}

/** 並列実行数ドット（インジケーター①）。 */
export function useHallucinationProgress(
  spaceId?: string,
  serviceId?: string
): UseHallucinationProgressResult {
  const { data, isLoading, error } = useQuery<DetectionProgressResponse | null>({
    queryKey: ['hallucination-detection-progress', spaceId, serviceId],
    queryFn: async ({ signal }) => {
      if (!spaceId || !serviceId) return null
      return getHallucinationDetectionProgress(spaceId, serviceId, signal)
    },
    enabled: Boolean(spaceId && serviceId),
    refetchInterval: HALLUCINATION_POLL_INTERVAL_MS,
  })

  return {
    running: data?.running ?? 0,
    limit: data?.limit ?? 0,
    isLoading,
    error: (error as Error | null) ?? null,
  }
}
